import { useQuery } from '@tanstack/react-query';
import { administrationClient, unwrap, createResource } from '@/shared/api-client';

export interface CountryPolicy {
  countryCode: string;
  defaultLanguage: string;
  defaultCurrency: string;
  defaultTimezone: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ReferenceItem {
  code: string;
  label: string;
}

export interface CountryReferenceData {
  countries: ReferenceItem[];
  languages: ReferenceItem[];
  currencies: ReferenceItem[];
  timezones: string[];
}

export interface CountryPolicyPage {
  items: CountryPolicy[];
  page: number;
  pageSize: number;
  total: number;
}

export interface CreateCountryPolicyInput {
  countryCode: string;
  defaultLanguage: string;
  defaultCurrency: string;
  defaultTimezone: string;
}

export interface UpdateCountryPolicyInput {
  defaultLanguage?: string;
  defaultCurrency?: string;
  defaultTimezone?: string;
  enabled?: boolean;
}

const referenceKey = ['country-policies', 'reference'] as const;

export const getCountryPolicies = (): Promise<CountryPolicyPage> =>
  unwrap(
    administrationClient.GET('/api/v1/franchise-administration/country-policies'),
  ) as Promise<CountryPolicyPage>;

export const getCountryReferenceData = (): Promise<CountryReferenceData> =>
  unwrap(
    administrationClient.GET('/api/v1/franchise-administration/country-policies/reference'),
  ) as Promise<CountryReferenceData>;

export const createCountryPolicy = (input: CreateCountryPolicyInput): Promise<CountryPolicy> =>
  unwrap(
    administrationClient.POST('/api/v1/franchise-administration/country-policies', { body: input }),
  ) as Promise<CountryPolicy>;

export const updateCountryPolicy = (
  countryCode: string,
  input: UpdateCountryPolicyInput,
): Promise<CountryPolicy> =>
  unwrap(
    administrationClient.PATCH('/api/v1/franchise-administration/country-policies/{code}', {
      params: { path: { code: countryCode } },
      body: input,
    }),
  ) as Promise<CountryPolicy>;

/** Country policies routed through the shared resource factory (ADR 0012); the
 *  update closure keys on `countryCode` rather than a synthetic id. */
const countryPolicies = createResource({
  key: ['country-policies'],
  list: getCountryPolicies,
  create: createCountryPolicy,
  update: ({ countryCode, input }: { countryCode: string; input: UpdateCountryPolicyInput }) =>
    updateCountryPolicy(countryCode, input),
});

export const useCountryPolicies = countryPolicies.useList;
export const useCreateCountryPolicy = countryPolicies.useCreate;
export const useUpdateCountryPolicy = countryPolicies.useUpdate;

export function useCountryReferenceData(enabled: boolean) {
  return useQuery({ queryKey: referenceKey, queryFn: getCountryReferenceData, enabled });
}
